import { type TourStepName } from "@common/contexts/Tour";

export namespace Basicprimitives {
  export namespace Builder {
    export type BuilderNode = {
      item: Basicprimitives.Builder.DiagramItem<any>;
      childIds: string[];
    };

    export type DiagramItemComponentProps<T> = {
      item: Basicprimitives.Builder.DiagramItem<T>;
      exit: () => void;
    };

    export type DiagramTemplateDescription<T> = Basicprimitives.TemplateConfig<Basicprimitives.FamilyItemConfig> & {
      ItemComponent?: React.ComponentType<DiagramItemComponentProps<T>>;
    };
    export type DiagramItemConnectorStyle = {
      color: Basicprimitives.Colors;
      lineType: Basicprimitives.LineType;
      opacity?: number;
      width?: number;
    };

    export type DiagramItem<T, TStep extends TourStepName = string> = Basicprimitives.FamilyItemConfig & {
      data?: T;
      connectorStyle?: DiagramItemConnectorStyle;
      tourStep?: TStep;
    };

    export type ContextDiagramItem<T> = DiagramItem<T> & {
      onClick: () => void;
    };
  }

  /**
   * The enumeration sets leftward or rightward item placement relative to the referenced item.
   * In the case of assistants or advisers, the referenced node is their immediate parent.
   * In the case of the family diagram, the referenced node is a sibling.
   *
   * @enum {AdviserPlacementType}
   */
  export enum AdviserPlacementType {
    /**
     * Auto select by layout manager
     */
    Auto = 0,
    /**
     * Left side
     */
    Left = 2,
    /**
     * Right side
     */
    Right = 3
  }

  /**
   * @typedef {number} AnnotationType
   **/

  /**
   * Defines type of on-screen and in-layout annotation object. Annotations are geometrical
   * figures drawn around or bound to existing nodes of the diagram.
   *
   * @enum {AnnotationType}
   */
  export enum AnnotationType {
    /**
     * The connector annotation displays a spline between two nodes of the diagram.
     * They are drawn on top and across the existing diagram layout, and they don't affect nodes placement.
     * If available space between nodes is not enough to display connector annotation,
     * then control draws it outside on the side of them.
     */
    Connector = 0,
    /**
     * Shape annotation is a possibility to draw some geometrical shapes over several nodes of the diagram.
     */
    Shape = 1,
    /**
     * Highlight path annotation traces a path between a given sequence of nodes over existing relation lines in the diagram.
     */
    HighlightPath = 2,
    /**
     * The in-layout label annotation display values over relation lines between nodes.
     * The control preserves space for labels in the diagram layout so they don't overlap nodes.
     * Label annotations are designed for auto-placement and bundling of connection lines between nodes when needed.
     */
    Label = 3,
    /**
     * The background annotation highlights nodes via drawing the rectangular shape in the node's background.
     * If the same style annotations of neighboring nodes overlap, control merges them into one continuous polygon geometry.
     */
    Background = 4,
    /**
     * Level annotation highlights same level nodes of the diagram via drawing continuous rectangular shape
     * from side to side in their background. Level has optional title on the side of the diagram view area.
     * Title may be placed inside or outside of the diagram. If it is placed inside, it is drawn in the
     * background and does not occupy space.
     */
    Level = 5
  }

  /**
   * @typedef {number} ChildrenPlacementType
   **/

  /**
   * The enumeration defines the shape of children's formation. By default, control places children in a horizontal
   * line below the parent node. On a large scale, this may result in the end-user having to scroll screens
   * to view all of them. To compensate, we provide the option of placing all of the children of a parent node
   * in a square/matrix formation. That will reduce sideways screen scrolling by compacting the child
   * nodes into a much smaller area on the screen.
   *
   * @enum {ChildrenPlacementType}
   */
  export enum ChildrenPlacementType {
    /**
     * Auto = This mode lets you set nodes layout at the component level and then redefine it for individual nodes if needed.
     */
    Auto = 0,
    /**
     * Vertical layout
     */
    Vertical = 1,
    /**
     * Horizontal layout
     */
    Horizontal = 2,
    /**
     * Matrix formation of the nodes
     */
    Matrix = 3
  }

  /**
   * @typedef {string} Colors
   **/

  /**
   * Just a list of named colors.
   *
   * @ignore
   * @enum {Colors}
   */
  export enum Colors {
    AliceBlue = "#f0f8ff",
    AntiqueWhite = "#faebd7",
    Aqua = "#00ffff",
    Aquamarine = "#7fffd4",
    Azure = "#f0ffff",

    Beige = "#f5f5dc",
    Bisque = "#ffe4c4",
    Black = "#000000",
    BlanchedAlmond = "#ffebcd",
    Blue = "#0000ff",
    BlueViolet = "#8a2be2",
    Brown = "#a52a2a",
    BurlyWood = "#deb887",
    Bronze = "#cd7f32",

    CadetBlue = "#5f9ea0",
    ChartReuse = "#7fff00",
    Chocolate = "#d2691e",
    Coral = "#ff7f50",
    CornflowerBlue = "#6495ed",
    Cornsilk = "#fff8dc",
    Crimson = "#dc143c",
    Cyan = "#00ffff",
    DarkBlue = "#00008b",
    DarkCyan = "#008b8b",
    DarkGoldenrod = "#b8860b",
    DarkGray = "#a9a9a9",
    DarkGreen = "#006400",
    DarkKhaki = "#bdb76b",
    DarkMagenta = "#8b008b",
    DarkOliveGreen = "#556b2f",
    Darkorange = "#ff8c00",
    DarkOrchid = "#9932cc",
    DarkRed = "#8b0000",
    DarkSalmon = "#e9967a",
    DarkSeaGreen = "#8fbc8f",
    DarkSlateBlue = "#483d8b",
    DarkSlateGray = "#2f4f4f",
    DarkTurquoise = "#00ced1",
    DarkViolet = "#9400d3",
    DeepPink = "#ff1493",
    DeepSkyBlue = "#00bfff",
    DimGray = "#696969",
    DodgerBlue = "#1e90ff",

    FireBrick = "#b22222",
    FloralWhite = "#fffaf0",
    ForestGreen = "#228b22",
    Fuchsia = "#ff00ff",

    Gainsboro = "#dcdcdc",
    GhostWhite = "#f8f8ff",
    Gold = "#ffd700",
    Goldenrod = "#daa520",
    Gray = "#808080",
    Green = "#008000",
    GreenYellow = "#adff2f",

    Honeydew = "#f0fff0",
    Hotpink = "#ff69b4",

    IndianRed = "#cd5c5c",
    Indigo = "#4b0082",
    Ivory = "#fffff0",
    Khaki = "#f0e68c",

    Lavender = "#e6e6fa",
    LavenderBlush = "#fff0f5",
    Lawngreen = "#7cfc00",
    Lemonchiffon = "#fffacd",
    LightBlue = "#add8e6",
    LightCoral = "#f08080",
    LightCyan = "#e0ffff",
    LightGoldenrodYellow = "#fafad2",

    LightGray = "#d3d3d3",
    LightGreen = "#90ee90",
    LightPink = "#ffb6c1",
    LightSalmon = "#ffa07a",
    LightSeaGreen = "#20b2aa",
    LightSkyBlue = "#87cefa",
    LightSlateGray = "#778899",
    LightSteelBlue = "#b0c4de",

    LightYellow = "#ffffe0",
    Lime = "#00ff00",
    Limegreen = "#32cd32",
    Linen = "#faf0e6",

    Magenta = "#ff00ff",
    Maroon = "#800000",
    MediumAquamarine = "#66cdaa",
    MediumBlue = "#0000cd",
    MediumOrchid = "#ba55d3",
    MediumPurple = "#9370d8",
    MediumSeaGreen = "#3cb371",
    MediumSlateBlue = "#7b68ee",

    MediumSpringGreen = "#00fa9a",
    MediumTurquoise = "#48d1cc",
    MediumVioletRed = "#c71585",
    MidnightBlue = "#191970",
    MintCream = "#f5fffa",
    MistyRose = "#ffe4e1",
    Moccasin = "#ffe4b5",

    NavajoWhite = "#ffdead",
    Navy = "#000080",

    Oldlace = "#fdf5e6",
    Olive = "#808000",
    Olivedrab = "#6b8e23",
    Orange = "#ffa500",
    OrangeRed = "#ff4500",
    Orchid = "#da70d6",

    PaleGoldenRod = "#eee8aa",
    PaleGreen = "#98fb98",
    PaleTurquoise = "#afeeee",
    PaleVioletRed = "#d87093",
    Papayawhip = "#ffefd5",
    Peachpuff = "#ffdab9",
    Peru = "#cd853f",
    Pink = "#ffc0cb",
    Plum = "#dda0dd",
    PowderBlue = "#b0e0e6",
    Purple = "#800080",

    Red = "#ff0000",
    RosyBrown = "#bc8f8f",
    RoyalBlue = "#4169e1",

    SaddleBrown = "#8b4513",
    Salmon = "#fa8072",
    SandyBrown = "#f4a460",
    SeaGreen = "#2e8b57",
    Seashell = "#fff5ee",
    Sienna = "#a0522d",
    Silver = "#c0c0c0",
    SkyBlue = "#87ceeb",
    SlateBlue = "#6a5acd",
    SlateGray = "#708090",
    Snow = "#fffafa",
    SpringGreen = "#00ff7f",
    SteelBlue = "#4682b4",

    Tan = "#d2b48c",
    Teal = "#008080",
    Thistle = "#d8bfd8",
    Tomato = "#ff6347",
    Turquoise = "#40e0d0",

    Violet = "#ee82ee",

    Wheat = "#f5deb3",
    White = "#ffffff",
    WhiteSmoke = "#f5f5f5",

    Yellow = "#ffff00",
    YellowGreen = "#9acd32"
  }

  /**
   * @typedef {number} ConnectorLabelPlacementType
   **/

  /**
   * Label placement relative to connector annotation. Connector annotation is bound and drawn between two nodes
   * defined by two properties = `fromItem` and `toItem`. Label can be placed close to "start", "end" nodes or in between of them
   *  along the connector line.
   *
   * @enum {ConnectorLabelPlacementType}
   */
  export enum ConnectorLabelPlacementType {
    From = 0,
    Between = 1,
    To = 2
  }

  /**
   * @typedef {number} ConnectorPlacementType
   **/

  /**
   * Connector placement type defines style of connector line drawing over diagram layout. It supports two options =
   * the `Straight` is classic direct line connecting two nodes, this is the most expected style of connector annotation
   * drawing over diagram, the second style is called `Offbeat` and it design to dynamically adopt to nodes mutual
   * location and gap between them. It uses free hand line style drawing going from start to the end nodes. Since every diagram
   * is packed with various connection lines, this annotation placement style is deliberately made not straight, so it can be
   * noticeable on top of other lines of the diagram.
   *
   * @enum {ConnectorPlacementType}
   */
  export enum ConnectorPlacementType {
    /**
     * Places connector annotation in the way that it does not overlap underlying diagram connector lines.
     * If nodes are close to each other and gap between them cannot fit annotation, then
     * it will be drawn on the side of the nodes, so it will have enough space for arrow and label.
     */
    Offbeat = 0,
    /**
     * Straight line annotation between nodes. This annotation mode provides basic conflict resolution between annotations
     * overlapping each other. If two or more straight annotations overlap each other then layout engine will
     * add extra offset to them, so they will be drawn in parallel to each other.
     */
    Straight = 1
  }

  /**
   * @typedef {number} ConnectorShapeType
   **/

  /**
   * Connector shape type defines number of lines and arrows at their ends drawn between nodes of the connector annotation.
   * This feature combined with basic conflict resolution, which places overlapping annotations in parallel when they overlap each other,
   * gives you full flexibility over variations of possible connector lines between two given nodes of diagram.
   *
   * @enum {ConnectorShapeType}
   */
  export enum ConnectorShapeType {
    /**
     * Single line with one arrow
     */
    OneWay = 0,
    /**
     * Two parallel lines with single arrows
     */
    TwoWay = 1,
    /**
     * Single line with 2 arrows.
     */
    BothWay = 2
  }

  export enum ConnectorStyleType {
    Extra = 0,
    Regular = 1,
    Highlight = 2
  }

  /**
   * @typedef {number} ConnectorType
   **/

  /**
   * Connection lines style. This option is only applicable to nodes minimized to markers or lines.
   * Full size nodes are always connected with squared connection lines
   *
   * @enum {ConnectorType}
   */
  export enum ConnectorType {
    /**
     * Orthogonal connection lines
     */
    Squared = 0,
    /**
     * Angular direct node to node connection lines
     */
    Angular = 1,
    /**
     * Curved direct node to node connection lines
     */
    Curved = 2
  }

  /**
   * @typedef {number} ElbowType
   **/

  /**
   * Elbow style of connection lines
   *
   * @enum {ElbowType}
   */
  export enum ElbowType {
    /**
     * No elbow
     */
    None = 0,
    /**
     * Dot marker at the intersection
     */
    Dot = 1,
    /**
     * Bevel elbow
     */
    Bevel = 2,
    /**
     * Round elbow
     */
    Round = 3
  }

  /**
   * Defines option state.
   *
   * @readonly
   * @enum {number}
   */
  export enum Enabled {
    /**
     * Option state is auto defined.
     */
    Auto = 0,
    /**
     * Enabled
     */
    True = 1,
    /**
     * Disabled
     */
    False = 2
  }

  /**
   * @typedef {number} GroupByType
   **/

  /**
   * This enumeration defines objects gravity in the chart relative to parents and children.
   * For example connection lines can be drawn with arrows, so this enumeration controls
   * direction of arrows up towards parents or down towards children in the hierarchy.
   * The other example is nodes placement close to their immediate parents or immediate children
   * in case when parents and children are offset from them by multiple levels in hierarchy.
   *
   * @enum {GroupByType}
   */
  export enum GroupByType {
    None = 0,
    Parents = 1,
    Children = 2
  }

  /**
   * @typedef {number} HorizontalAlignmentType
   **/

  /**
   * Horizontal alignment
   *
   * @enum {HorizontalAlignmentType}
   */
  export enum HorizontalAlignmentType {
    Center = 0,
    Left = 1,
    Right = 2
  }

  /**
   * @typedef {number} ItemType
   **/

  /**
   * The enumeration defines child node placement relative to its parent node.
   * By default, the control places all children that belong to the same parent as
   * the horizontal line below it. That works for a regular hierarchy of nodes
   * having the same type. In the organizational chart, we have a lot of exceptions
   * and non-hierarchical relations between nodes. For this purpose, we provide custom
   * item types to place nodes around the logical parent, which helps visually
   * differentiate nodes logically belonging to the same parent.  These placement
   * options give a lot of space for creating roles such as an Assistant, Adviser,
   * various Partners, and co-heads in the organization.  Additionally, control allows
   * shaping the same type of children into vertical and matrix formations
   * and place them into multiple levels.
   *
   * @enum {ItemType}
   */
  export enum ItemType {
    /**
     * The regular type places node below parent.
     */
    Regular = 0,
    /**
     * The assistant type places node at the row on the side of the connection line going from parent to its children.
     */
    Assistant = 1,
    /**
     * The sub-assistant node type is a variation of the assistant node type.
     * The sub-assistant places node at the row on the side of the connection line
     * going from parent to its children. The connection line goes out of the top side of the node.
     */
    SubAssistant = 4,
    /**
     * The adviser type places node on the right or left side of the parent.
     */
    Adviser = 2,
    /**
     * The sub-adviser type places node on the right or left side of the parent.
     * The connection line goes out of the top side of the sub-adviser node.
     */
    SubAdviser = 5,
    /**
     * The general partner type is an imitation of multiple inheritances in the organizational chart hierarchy.
     * The general-partner node is drawn side by side with its logical parent, and control places
     * regular children below them, so they visually look like parents. Another layout specifics
     * of the general partner is that it is connected to its logical parent's parent.
     * So visually, it becomes an immediate child of its grandparent.
     */
    GeneralPartner = 6,
    /**
     * The limited partner is a variation of the general partner.
     * The only difference is that it is not connected to its logical grandparent.
     */
    LimitedPartner = 7,
    /**
     * The adviser partner is a variation of the limited partner.
     * The difference is that it has an extra connection line to its logical parent.
     */
    AdviserPartner = 8
  }

  export enum LabelType {
    Regular = 0,
    Dummy = 1,
    Fixed = 2,
    None = 3
  }

  export enum Layers {
    LevelAnnotation = 1,
    BackgroundAnnotation = 2,
    BackgroundAnnotations = 3,
    BackgroundConnectorAnnotation = 4,
    BackgroundHighlightPathAnnotations = 5,
    Connector = 6,
    ForegroundHighlightPathAnnotations = 7,
    Highlight = 8,
    Marker = 9,
    Label = 10,
    Cursor = 11,
    Item = 12,
    ForegroundAnnotations = 13,
    ForegroundConnectorAnnotation = 14,
    Annotation = 15,
    Controls = 16
  }

  /**
   * @typedef {number} LineType
   **/

  /**
   * Line style of connection lines.
   *
   * @enum {LineType}
   */
  export enum LineType {
    Solid = 0,
    Dotted = 1,
    Dashed = 2
  }

  /**
   * @typedef {number} NavigationMode
   **/

  /**
   * Interactivity mode. Control implements standard behavior of classic desktop UI controls. It supports single selected node - cursor.
   * It supports on mouse over node visual feedback - highlight. It supports selection of group of nodes - selected items.
   * All that functionality can be disabled depending on your application requirements.
   *
   * @enum {NavigationMode}
   */
  export enum NavigationMode {
    /**
     * Everything is on.
     */
    Default = 0,
    /**
     * Cursor selection only without highlight.
     */
    CursorOnly = 1,
    /**
     * Mouse over feedback only
     */
    HighlightOnly = 3,
    /**
     * No interactivity
     */
    Inactive = 2
  }

  /**
   * @typedef {number} NavigationMode
   **/

  /**
   * Neighbors selection mode. The control supports diagram auto fit into screen view. It is achieved via drawing nodes in form of markers.
   * So small nodes make diagram fit into the screen space, but they have no details. Our solution is to show cursor and selected items
   * of the diagram in full size and draw all other as markers.
   *
   * This enumeration controls visibility of neighbors of the cursor node in the auto fit mode. It allows to draw
   * them in full size regardless of available space.
   *
   * @enum {NavigationMode}
   */
  export enum NeighboursSelectionMode {
    /**
     * Selects parents and children of the cursor item
     */
    ParentsAndChildren = 0,
    /**
     * Selects parents, children, and siblings of the cursor item.
     */
    ParentsChildrenSiblingsAndSpouses = 1
  }

  /**
   * @typedef {number} OrientationType
   **/

  /**
   * Controls diagram layout orientation. The control can be rotated in any direction, this is needed for arabic support and various layout.
   *
   * @enum {OrientationType}
   */
  export enum OrientationType {
    Top = 0,
    Bottom = 1,
    Left = 2,
    Right = 3,
    None = 4
  }

  /**
   * @typedef {number} PageFitMode
   **/

  /**
   * Fits diagram into available screen space. When diagram size significantly larger that available screen space, its scrolling and navigation
   * becomes problematic, so we support automatic diagram fit into the screen space via rendering some of its nodes in form of small markers.
   * Control supports several page fit mode options which can match your requirements depending on diagram layout, orientation and number of nodes.
   *
   * Autosize - this option is opposite to auto fit. It lets you expand control size to fit all diagram nodes full size without scroll bars.
   *
   * @enum {PageFitMode}
   */
  export enum PageFitMode {
    /**
     * Disabled. All nodes are being rendered using their templates.
     */
    None = 0,
    /**
     * Fits diagram into the view width, so it has no horizontal scrollbar.
     */
    PageWidth = 1,
    /**
     * Fits diagram into the view hight, so it has no vertical scrollbar.
     */
    PageHeight = 2,
    /**
     * Fits diagram into the view so it has no scroll bars.
     */
    FitToPage = 3,
    /**
     * This is opposite mode to auto fit. In this mode diagram controls its size, it sets its size to fit all nodes and render them full size using templates.
     */
    AutoSize = 5,
    /**
     * Renders all nodes as markers regardless of available screen space. Control selects and renders full size cursor, its neighbours and selected nodes only.
     * Don't forget to disable selection path as well, so nodes from cursor up to the root are not selected.
     */
    SelectionOnly = 6
  }

  /**
   * @typedef {number} PlacementType
   **/

  /**
   * Defines element placement relative to rectangular area it is bound to.
   *
   * @enum {PlacementType}
   */
  export enum PlacementType {
    /**
     * Defined by other control options.
     */
    Auto = 0,
    TopLeft = 8,
    Top = 1,
    TopRight = 2,
    RightTop = 11,
    Right = 3,
    RightBottom = 12,
    BottomRight = 4,
    Bottom = 5,
    BottomLeft = 6,
    LeftBottom = 10,
    Left = 7,
    LeftTop = 9
  }

  /**
   * @typedef {number} RenderingMode
   **/

  /**
   * This enumeration is used to tell rendering callback functions current state of the template.
   * It is needed for proper events binding and content updates.
   *
   * @enum {RenderingMode}
   */
  export enum RenderingMode {
    /**
     * Template is just created.
     */
    Create = 0,
    /**
     * Template is reused and update is needed.
     */
    Update = 1
  }

  export enum SegmentType {
    Line = 0,
    Move = 1,
    QuadraticArc = 2,
    CubicArc = 3,
    Dot = 4
  }

  /**
   * @typedef {number} SelectionPathMode
   **/

  /**
   * Selection path mode. This enumeration controls visibility of nodes between cursor and the root of the diagram in the auto fit mode. It allows to draw
   * them in full size regardless of available space and auto fit mode.
   *
   * The control supports diagram auto fit into screen view. It is achieved via drawing nodes in form of markers.
   * So small nodes make diagram fit into the screen space, but they have no details. Our solution is to show cursor and selected items
   * of the diagram in full size and draw all other diagram nodes as markers.
   *
   * @enum {SelectionPathMode}
   */
  export enum SelectionPathMode {
    /**
     * No selection path
     */
    None = 0,
    /**
     * Selects cursor node parents up to the root are renders them full size regardless of available space.
     */
    FullStack = 1
  }

  /**
   * @typedef {number} ShapeType
   **/

  /**
   * Shapes
   *
   * @enum {ShapeType}
   */
  export enum ShapeType {
    Rectangle = 0,
    Oval = 1,
    Triangle = 2,
    CrossOut = 3,
    Circle = 4,
    Rhombus = 5,
    Wedge = 7,
    FramedOval = 8,
    FramedTriangle = 9,
    FramedWedge = 10,
    FramedRhombus = 11,
    None = 6
  }

  export enum SideFlag {
    Top = 1,
    Right = 2,
    Bottom = 4,
    Left = 8
  }

  /**
   * @typedef {number} TextOrientationType
   **/

  /**
   * Text rotation
   *
   * @enum {TextOrientationType}
   */
  export enum TextOrientationType {
    /**
     * Regular horizontal text
     */
    Horizontal = 0,
    /**
     * Rotate text left for 90 degree.
     */
    RotateLeft = 1,
    /**
     * Rotate text right for 90 degree.
     */
    RotateRight = 2,
    /**
     * Depends on other options.
     */
    Auto = 3
  }

  /**
   * @typedef {number} UpdateMode
   **/

  /**
   * Controls update of the diagram
   *
   * @enum {UpdateMode}
   */
  export enum UpdateMode {
    /**
     * Forces control to make a full chart redraw. It is equivalent to initial chart creation.
     * It removes everything from placeholder and renders all elements again.
     */
    Recreate = 0,
    /**
     * Optimized refresh. It only updates visual elements which needs to be updated.
     */
    Refresh = 1,
    /**
     * Updates highlight position only
     */
    PositonHighlight = 2
  }

  /**
   * @typedef {number} VectorRelationType
   **/

  /**
   * Defines relation between two vectors
   *
   * @enum {VectorRelationType}
   */
  export enum VectorRelationType {
    None = 0,
    Null = 1,
    Collinear = 2,
    Opposite = 3
  }

  /**
   * @typedef {number} VerticalAlignmentType
   **/

  /**
   * Controls nodes vertical alignment inside row of nodes. If row of nodes contains nodes of
   * multiple sizes then small nodes are vertically aligned relative to their large neighbours.
   *
   * @enum {VerticalAlignmentType}
   */
  export enum VerticalAlignmentType {
    Top = 0,
    Middle = 1,
    Bottom = 2
  }

  /**
   * @typedef {number} Visibility
   **/

  /**
   * Minimal nodes visibility in the diagram. If auto fit of diagram into current page size is enabled, then
   * this option controls minimum allowed size of diagram nodes.
   *
   * @enum {Visibility}
   */
  export enum Visibility {
    /**
     * Selects best visibility mode.
     */
    Auto = 0,
    /**
     * Regular template based diagram nodes
     */
    Normal = 1,
    /**
     * Diagram draws nodes in form of markers
     */
    Dot = 2,
    /**
     * Diagram only draws connection lines and hides actual nodes.
     */
    Line = 3,
    /**
     * Makes node invisible in layout. If node has no parents then
     * its connection lines are hidden as well.
     *
     * @ignore
     */
    Invisible = 4
  }

  /**
   * @typedef {number} ZOrderType
   **/

  /**
   * Option to draw annotation in the foreground or in the background of diagram nodes.
   *
   * @enum {ZOrderType}
   */
  export enum ZOrderType {
    /**
     * Depends on annotation type.
     */
    Auto = 0,
    /**
     * Background
     */
    Background = 1,
    /**
     * Foreground
     */
    Foreground = 2
  }

  export interface Thickness {
    left: number;
    top: number;
    right: number;
    bottom: number;
  }

  export interface ShapeAnnotationConfig {
    annotationType?: AnnotationType;
    /** Shape border line color */
    borderColor?: string;
    color?: Colors;
    /** Corner radius. Body corner radius in percents or pixels. For applicable shapes only. */
    cornerRadius?: string;
    /** Shape fill color */
    fillColor?: string;
    /** Collection of nodes ids this shape annotation is drawn for. */
    items?: string[];
    /** Label. Label styled with css class name "bp-connector-label". */
    label?: string;
    /** Label offset from shape in pixels. */
    labelOffset?: number;
    /** Label placement relative to the annotation. */
    labelPlacement?: PlacementType;
    labelSize?: Size;
    lineType?: LineType;
    lineWidth?: number;
    /** Sets shape offset around annotated items. */
    offset?: Thickness;
    /** Background color opacity. */
    opacity?: number;
    /** If true then annotated nodes are shown full size regardless of controls auto fit mode and available screen space. */
    selectItems?: boolean;
    shapeType?: ShapeType;
    /** Sets annotation Z order placement relative to the diagram items. Diagram visual elements are drawn in layers on top of each other. If you place annotations over diagram nodes then you block mouse events of UI elements in their templates. Browsers don't support mouse events transparency consistently yet. So in order to avoid mouse events blocking of UI elements in item templates you have to place annotation items under them or explicitly define maximum zindex for controls and make them rendered on top of other visual elements. The control takes this into account and renders buttons panel on top of everything, so they are never blocked by annotations drawn in front of diagram nodes. */
    zOrderType?: ZOrderType;
  }

  export interface FamilyItemConfig {
    /* Add to matrix property allows node to be grouped with other nodes into matrix. It is true by default. */
    addToMatrix?: boolean;
    /* Shows selection check box for the node. If Auto then selection check box visibility depends on control's configuration. Auto - depends on hasSelectorCheckbox property of the control True - shown False - hidden */
    hasSelectorCheckbox?: Enabled;
    /** Item id. It should be unique per chart. */
    id: string;
    /** Parents items ids. If this collection is empty then item considered as a root item. */
    parents: string[];
    templateName: string;
    matrixId?: string;
    noHighlight?: boolean;
    position?: number;
    placementType?: AdviserPlacementType;
    relativeItem?: string;
  }

  export interface FamilyDiagramConfig<T extends FamilyItemConfig> {
    /** This option keeps items at the same levels after connections bundling. */
    alignBylevels?: boolean;
    annotations?: ShapeAnnotationConfig[];
    arrowsDirection?: GroupByType;
    autoSizeMaximum?: Size;
    /** Sets minimum size the diagram can shrink itself in auto size mode. See pageFitMode property. In the auto size mode diagram controls its placeholder size itself, it sets its size to accommodate all nodes and render them normally. */
    autoSizeMinimum?: Size;
    buttonsPanelSize?: number;
    calloutBorderColor?: string;
    calloutCornerRadius?: number;
    /** Callout annotation border line offset. */
    calloutOffset?: number;
    /** Callout annotation opacity */
    calloutOpacity?: number;
    /** Callout annotation placement offset. Sets how far callout content is offset from the marker it is displayed for. */
    calloutPlacementOffset?: number;
    /** Callout annotation pointer width. */
    calloutPointerWidth?: string;
    connectorType?: ConnectorType;
    /* The cousins interval multiplier property adds extra space between branches of the hierarchy. For example, if the multiplier equals five, nodes of the same parent will have interval 20, and nodes of two different parents will have interval 100. */
    cousinsIntervalMultiplier?: number;
    /** Cursor item. Organization Chart control has API options equivalent to standard UI controls. The cursor item is used to select a single item in the hierarchy with a mouse click, and the highlighted item provides visual feedback on the mouse over. Selected items collection is equivalent to checked items in ListView or TreeView controls. The chart's navigation work around the current cursor item. The component shows the cursor and its neighbors regardless of page fit mode. So cursor item plays the role of local zoom in the chart hierarchy. The user navigates around the chart via clicking and selecting cursor items and zooming into data around the new cursor item. The control notifies about this property changes with onCursorChanging and onCursorChanged events. If the cursor item is set to null, then no cursor item is selected in the diagram. */
    cursorItem?: string | null;
    /** Sets interval between nodes of the same row, minimized down to markers. */
    dotItemsInterval?: number;
    /** Sets the spacing after the row containing nodes minimized down to markers. */
    dotLevelShift?: number;
    enableMatrixLayout?: boolean;
    enablePanning?: boolean;
    /** Sets buttons visibility. Auto - cursor item only. True - visible False - hidden */
    hasButtons?: Enabled;
    /** Shows selection check box for the node. If Auto then selection check box visibility depends on control's configuration. Auto - depends on hasSelectorCheckbox property of the control True - shown False - hidden */
    hasSelectorCheckbox?: Enabled;
    /** Highlight gravity radius. This property controls mouse over feedback and callout annotation visibility for nodes rendered as markers when diagram auto fits nodes into available screen space. It makes marker highlighted when mouse pointer is inside of the gravity radius cycle of the marker. This property is ignored when the nearest item is outside of the screen boundaries and is not visible to the end user. The normal item has mouse over feedback in form of highlight border only when mouse pointer is inside of its boundaries. */
    highlightGravityRadius?: number;
    /** Highlighted item. Shows highlight and callout annotation for given item id. It does not trigger diagram layout or scrolling so it can be used to synchronize mouse over feedback of the diagram nodes with other collection controls or UI elements. The control notifies about this property changes with onHighlightChanging and onHighlightChanged events. If null then no highlight shown on the diagram. */
    highlightItem?: string | null;
    /** Sets children horizontal alignment relative to their parent. The children by default are measured in size and then aligned towards the parent node. If it is Center aligned then parent node is placed in the middle of the children. In the Left alignment mode parent is aligned to left of the children and vice versa for Right alignment. */
    horizontalAlignment?: HorizontalAlignmentType;
    frameInnerPadding?: Thickness;
    /** This property sets loose nodes alignment between rows. Nodes can be placed close towards parents or children. */
    groupByType?: GroupByType;
    groupTitleFontFamily?: string;
    labelFontFamily?: string;
    levelTitleFontFamily?: string;
    linesWidth?: number;
    linesColor?: string;
    navigationMode: NavigationMode;
    /** Sets interval between nodes of the same row. */
    normalItemsInterval?: number;
    /** Sets the spacing between rows. */
    normalLevelShift?: number;
    /** Items collection. Ths property defines data we render in the diagram. Every item should have set unique id property. They are used to create relations between items in the diagram and for rendering of various UI elements bound to nodes. */
    items: T[];
    /** On buttons panel render event. This callback function is called to render context of buttons panel. It is used to replace buttons collection property in ReactJS component. So we preserve context buttons panel as a functional concept, but eliminate buttons customization API. */
    onButtonsRender?: OnButtonsRender<T>;
    padding?: Thickness;
    /** Page fit mode. Minimizing nodes into markers and labels. This option provides a special mode that renders the diagram nodes in the form of markers. This is a highly scalable form that is capable of rendering large numbers of nodes while not affecting the rendering performance. With this, huge diagrams can be fit into available screen space. When using a graphics editor to manually draw your diagrams, it is common place to have large gaps between the nodes. This can make the diagram/chart unreadable, hard to edit and navigate. On top of that, on a large scale the diagram could have screen size intervals between items. Admittedly the computer UI does allow the user to scale and fit the diagram in order to visualize it on a single screen. But in that case, the items become small and unreadable as there is no scaling priority and the items are just too small to be readable. */
    pageFitMode?: PageFitMode;
    showCallout?: boolean;
    /** Sets labels visibility for nodes when they are minimized into markers by page auto fit. See pageFitMode property. The control does not preserve space for labels in the diagram layout, since that would contradict the purpose of minimizing the nodes into markers. Use controls dotLevelShift, dotItemsInterval properties to preserve space between nodes for labels. Labels are displayed inside of divs of the fixed size, see labelSize property, and control provides simple conflict resolution to avoid labels overlapping. If two labels overlap each other with their bounding rectangles then only one of them is going to stay visible. Auto - displays label only when it has space to be rendered. True - shows label regardless, even if it overlaps other labels and nodes. False - hidden. */
    showLabels?: Enabled;
    /** Collection of named templates used to define content for nodes, cursor and highlight. By default control provides templates for all types of visual elements. */
    templates?: TemplateConfig<T>[];
    verticalAlignment?: VerticalAlignmentType;
  }

  export interface FamilyDiagramProps<T extends FamilyItemConfig> {
    centerOnCursor?: boolean;
    config: FamilyDiagramConfig<T>;

    onCursorChanging?: OnChangingEvent<T>;
    onCursorChange?: OnChangingEvent<T>;
    onHighlightChanging?: OnChangingEvent<T>;
  }

  export interface OrgDiagramConfig<T extends OrgItemConfig> {
    arrowsDirection?: GroupByType;
    /** Sets minimum size the diagram can shrink itself in auto size mode. See pageFitMode property. In the auto size mode diagram controls its placeholder size itself, it sets its size to accommodate all nodes and render them normally. */
    autoSizeMinimum?: Size;
    buttonsPanelSize?: number;
    calloutCornerRadius?: number;
    /** Callout annotation border line offset. */
    calloutOffset?: number;
    /** Callout annotation opacity */
    calloutOpacity?: number;
    /** Callout annotation placement offset. Sets how far callout content is offset from the marker it is displayed for. */
    calloutPlacementOffset?: number;
    /** Callout annotation pointer width. */
    calloutPointerWidth?: string;
    connectorType?: ConnectorType;
    /** Cursor item. Organization Chart control has API options equivalent to standard UI controls. The cursor item is used to select a single item in the hierarchy with a mouse click, and the highlighted item provides visual feedback on the mouse over. Selected items collection is equivalent to checked items in ListView or TreeView controls. The chart's navigation work around the current cursor item. The component shows the cursor and its neighbors regardless of page fit mode. So cursor item plays the role of local zoom in the chart hierarchy. The user navigates around the chart via clicking and selecting cursor items and zooming into data around the new cursor item. The control notifies about this property changes with onCursorChanging and onCursorChanged events. If the cursor item is set to null, then no cursor item is selected in the diagram. */
    cursorItem?: string | null;
    /** Sets interval between nodes of the same row, minimized down to markers. */
    dotItemsInterval?: number;
    /** Sets the spacing after the row containing nodes minimized down to markers. */
    dotLevelShift?: number;
    /** Sets buttons visibility. Auto - cursor item only. True - visible False - hidden */
    hasButtons?: Enabled;
    /** Shows selection check box for the node. If Auto then selection check box visibility depends on control's configuration. Auto - depends on hasSelectorCheckbox property of the control True - shown False - hidden */
    hasSelectorCheckbox?: Enabled;
    /** Highlight gravity radius. This property controls mouse over feedback and callout annotation visibility for nodes rendered as markers when diagram auto fits nodes into available screen space. It makes marker highlighted when mouse pointer is inside of the gravity radius cycle of the marker. This property is ignored when the nearest item is outside of the screen boundaries and is not visible to the end user. The normal item has mouse over feedback in form of highlight border only when mouse pointer is inside of its boundaries. */
    highlightGravityRadius?: number;
    /** Highlighted item. Shows highlight and callout annotation for given item id. It does not trigger diagram layout or scrolling so it can be used to synchronize mouse over feedback of the diagram nodes with other collection controls or UI elements. The control notifies about this property changes with onHighlightChanging and onHighlightChanged events. If null then no highlight shown on the diagram. */
    highlightItem?: string | null;
    /** Sets children horizontal alignment relative to their parent. The children by default are measured in size and then aligned towards the parent node. If it is Center aligned then parent node is placed in the middle of the children. In the Left alignment mode parent is aligned to left of the children and vice versa for Right alignment. */
    horizontalAlignment?: HorizontalAlignmentType;
    linesWidth?: number;
    linesColor?: string;
    /** Sets interval between nodes of the same row. */
    normalItemsInterval?: number;
    /** Sets the spacing between rows. */
    normalLevelShift?: number;
    /** Items collection. Ths property defines data we render in the diagram. Every item should have set unique id property. They are used to create relations between items in the diagram and for rendering of various UI elements bound to nodes. */
    items: T[];
    /** On buttons panel render event. This callback function is called to render context of buttons panel. It is used to replace buttons collection property in ReactJS component. So we preserve context buttons panel as a functional concept, but eliminate buttons customization API. */
    onButtonsRender?: OnButtonsRender<T>;
    /** Page fit mode. Minimizing nodes into markers and labels. This option provides a special mode that renders the diagram nodes in the form of markers. This is a highly scalable form that is capable of rendering large numbers of nodes while not affecting the rendering performance. With this, huge diagrams can be fit into available screen space. When using a graphics editor to manually draw your diagrams, it is common place to have large gaps between the nodes. This can make the diagram/chart unreadable, hard to edit and navigate. On top of that, on a large scale the diagram could have screen size intervals between items. Admittedly the computer UI does allow the user to scale and fit the diagram in order to visualize it on a single screen. But in that case, the items become small and unreadable as there is no scaling priority and the items are just too small to be readable. */
    pageFitMode?: PageFitMode;
    /** Sets labels visibility for nodes when they are minimized into markers by page auto fit. See pageFitMode property. The control does not preserve space for labels in the diagram layout, since that would contradict the purpose of minimizing the nodes into markers. Use controls dotLevelShift, dotItemsInterval properties to preserve space between nodes for labels. Labels are displayed inside of divs of the fixed size, see labelSize property, and control provides simple conflict resolution to avoid labels overlapping. If two labels overlap each other with their bounding rectangles then only one of them is going to stay visible. Auto - displays label only when it has space to be rendered. True - shows label regardless, even if it overlaps other labels and nodes. False - hidden. */
    showLabels?: Enabled;
    /** Collection of named templates used to define content for nodes, cursor and highlight. By default control provides templates for all types of visual elements. */
    templates?: TemplateConfig<T>[];
  }

  export interface OrgDiagramProps<T extends OrgItemConfig> {
    centerOnCursor?: boolean;
    config: OrgDiagramConfig<T>;

    onCursorChanging?: OnChangingEvent<T>;
    onHighlightChanging?: OnChangingEvent<T>;
  }

  export interface OrgItemConfig {
    /** Item id. It should be unique per chart. */
    id: string | number;
    /** Parent item id. If null then node is the root item of the hierarchy. */
    parent: string | null;
    hasButtons?: Enabled;
    itemTitleColor?: string;
    templateName: string;
    noHighlight?: boolean;
  }

  export type OnChangingEvent<T> = (event: any, data: { context: T }) => boolean;
  export type MouseEvent<T> = (event: any, data: { context: T }) => void;
  export type OnButtonsRender<T> = (event: OnItemRenderEvent<T>) => any;

  /** Callback function for rendering content of the diagram nodes. This callback is only called when custom item template is defined in the template object configuration. This callback receives reference to DOM element and context object of the rendered item. The control reuses existing elements in the DOM, so it is applications responsibility to properly update their content. */
  export type OnItemRender<T> = (event: OnItemRenderEvent<T>) => any;

  export interface OnItemRenderEvent<T> {
    /** Node id */
    id: string | number;
    /** Context object of the node */
    context: T;
    hasButtons: boolean;
    hasGroupTitle: boolean;
    hasSelectorCheckbox: boolean;
    /** The rendered item is current diagram cursor item */
    isCursor: boolean;
    /** The rendered item is selected */
    isSelected: boolean;
    /** This option indicates current template state. */
    renderingMode: RenderingMode | null;
    /** Node template name */
    templateName: string | null;
  }

  export interface Padding {
    bottom: number;
    left: number;
    right: number;
    top: number;
  }

  export interface Size {
    height: number;
    width: number;
  }

  export interface TemplateConfig<T> {
    cursorBorderWidth?: number;
    cursorPadding?: Padding;
    highlightBorderWidth?: number;
    highlightPadding?: Padding;
    itemSize?: Size;
    minimizedItemSize?: Size;
    name: string;
    onItemRender: OnItemRender<T>;
    onCursorRender?: () => any;
  }
}
